import { SiteDto } from "@growth/dto";
import { SiteTheme, siteThemeSchema } from "@growth/schema";
import { Button, Field, Input, Textarea } from "@growth/ui";
import { LoaderCircle, Save } from "lucide-react";
import { type FormEvent, useState } from "react";

import { ErrorText, PageHeader, selectClass } from "../components/page-header";
import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import { useSaveSite, useSite } from "../lib/workspace";

const THEME_LABELS: Record<SiteTheme, string> = {
  brand: "From the brand kit (name, logo, colours)",
  firstround: "First Round (folio)",
  apex25: "Apex 25",
};

/** The active brand's blog: where it's served, how it looks, and analytics. */
export const BlogSettingsPage = () => {
  const { brand } = useActive();
  const site = useSite();
  return (
    <section className="max-w-3xl space-y-6">
      <PageHeader eyebrow={brand?.name ?? ""} title="Blog settings">
        Posts are served at <code>/blog</code> on every host listed here.
      </PageHeader>
      {site.isError && <ErrorText>{apiErrorMessage(site.error)}</ErrorText>}
      {site.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}
      {!site.isPending && !site.isError && (
        <SiteForm
          key={site.data?.id ?? "new"}
          site={site.data ?? null}
          brandName={brand?.name ?? ""}
        />
      )}
    </section>
  );
};

const SiteForm = ({ site, brandName }: { site: SiteDto | null; brandName: string }) => {
  const { atLeast } = useActive();
  const isOwner = atLeast("OWNER");
  const save = useSaveSite();
  const [form, setForm] = useState({
    name: site?.name ?? brandName,
    domain: site?.domain ?? "",
    theme: site?.theme ?? ("brand" as SiteTheme),
    hosts: site?.hosts.join("\n") ?? "",
    ga4MeasurementId: site?.ga4MeasurementId ?? "",
  });
  const set = (key: keyof typeof form, value: string) => setForm({ ...form, [key]: value });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate({
      name: form.name,
      domain: form.domain,
      theme: form.theme,
      hosts: form.hosts
        .split(/[\s,]+/)
        .map((host) => host.trim())
        .filter(Boolean),
      ga4MeasurementId: form.ga4MeasurementId.trim() || null,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4 rounded-xl border border-line bg-white p-5">
      {!site && (
        <p className="text-sm text-muted">
          This brand has no blog yet. Fill this in to create one.
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Blog name">
          {(props) => (
            <Input
              {...props}
              required
              disabled={!isOwner}
              value={form.name}
              onChange={(event) => set("name", event.target.value)}
            />
          )}
        </Field>
        <Field label="Canonical domain" hint="Links and search results use https://<this>/blog">
          {(props) => (
            <Input
              {...props}
              required
              disabled={!isOwner}
              placeholder="example.com"
              value={form.domain}
              onChange={(event) => set("domain", event.target.value)}
            />
          )}
        </Field>
        <Field label="Look">
          {(props) => (
            <select
              {...props}
              className={selectClass}
              disabled={!isOwner}
              value={form.theme}
              onChange={(event) => set("theme", event.target.value)}
            >
              {siteThemeSchema.options.map((theme) => (
                <option key={theme} value={theme}>
                  {THEME_LABELS[theme]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="GA4 measurement id (optional)">
          {(props) => (
            <Input
              {...props}
              disabled={!isOwner}
              placeholder="G-…"
              value={form.ga4MeasurementId}
              onChange={(event) => set("ga4MeasurementId", event.target.value)}
            />
          )}
        </Field>
      </div>
      <Field
        label="Hosts that serve this blog"
        hint="One per line. Each host can serve one blog only, across every workspace."
      >
        {(props) => (
          <Textarea
            {...props}
            required
            rows={4}
            disabled={!isOwner}
            placeholder={"blog.example.com\nexample.localhost"}
            value={form.hosts}
            onChange={(event) => set("hosts", event.target.value)}
            className="font-mono text-[13px]"
          />
        )}
      </Field>
      {save.isError && <ErrorText>{apiErrorMessage(save.error)}</ErrorText>}
      {isOwner ? (
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : (
            <Save className="size-4" />
          )}
          {site ? "Save" : "Create blog"}
        </Button>
      ) : (
        <p className="text-xs text-muted">Only owners can change these.</p>
      )}
    </form>
  );
};
